(function () {
  'use strict';

  function campaignState(config, now = Date.now()) {
    const start = Date.parse(config.startsAt);
    const end = Date.parse(config.endsAt);
    if (!config.enabled || !config.code || !Number.isFinite(start) || !Number.isFinite(end)
      || end <= start || !(config.percent > 0 && config.percent <= 100)) return 'disabled';
    return now < start ? 'upcoming' : now >= end ? 'expired' : 'active';
  }

  function estimatedPrice(cents, percent) {
    return Math.round(Number(cents) * (100 - percent) / 100);
  }

  function campaignVersion(config) {
    return JSON.stringify([config.campaign, config.enabled, config.code, config.percent,
      config.startsAt, config.endsAt, config.productIds]);
  }

  function validContext(record, config, now = Date.now()) {
    return record === campaignVersion(config) && campaignState(config, now) === 'active';
  }

  function productLink(path, config, now = Date.now()) {
    if (campaignState(config, now) !== 'active') return path;
    const url = new URL(path, 'https://store.invalid');
    url.searchParams.set('promo', config.campaign);
    // Shopify forwards outer parameters, replacing any query inside redirect.
    return '/discount/' + encodeURIComponent(config.code) + '?redirect='
      + encodeURIComponent(url.pathname) + '&' + url.searchParams.toString() + url.hash;
  }

  function applications(cart) {
    return [
      ...(cart.cart_level_discount_applications || []),
      ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || [])
        .map(allocation => ({ ...allocation.discount_application, allocated: allocation.amount })))
    ];
  }

  function discountCodes(cart, draft = '') {
    const codes = [
      ...(cart.discount_codes || []).map(entry => typeof entry === 'string' ? entry : entry.code),
      ...applications(cart).filter(entry => entry.type === 'discount_code').map(entry => entry.title),
      ...draft.split(',')
    ];
    return [...new Set(codes.filter(Boolean).map(code => code.trim().toUpperCase()).filter(Boolean))];
  }

  function variantQuantity(cart, variantId) {
    return (cart.items || []).filter(item => String(item.variant_id) === String(variantId))
      .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  }

  function purchaseOutcome(cart, attempt) {
    if (!cart.token || cart.token !== attempt.token) return 'different_cart';
    if (variantQuantity(cart, attempt.variantId) > attempt.baseline) return 'added';
    return attempt.stage === 'pending' ? 'pending' : 'not_added';
  }

  function discountOutcome(cart, code, productIds, variantId) {
    const items = (cart.items || []).filter(item => productIds.map(String).includes(String(item.product_id))
      && (variantId == null || String(item.variant_id) === String(variantId)));
    if (!items.length) return 'registered';
    const discounted = item => (item.line_level_discount_allocations || []).some(allocation =>
      allocation.discount_application?.type === 'discount_code'
      && allocation.discount_application.title?.toUpperCase() === code.toUpperCase()
      && Number(allocation.amount) > 0);
    const applied = variantId == null ? items.some(discounted) : items.every(discounted);
    return applied ? 'applied' : 'unavailable';
  }

  function createCartClient(root, fetcher = globalThis.fetch.bind(globalThis)) {
    async function request(path, body) {
      const response = await fetcher(root + path, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        ...(body ? { body: JSON.stringify(body) } : {}),
        signal: AbortSignal.timeout(15000)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Cart request failed');
      return data;
    }
    return {
      read: () => request('cart.js'),
      refresh: sections => request('cart/update.js', { sections }),
      async apply(code, draft, sections) {
        const cart = await request('cart.js');
        const codes = discountCodes(cart, draft);
        if (!codes.includes(code.toUpperCase())) codes.push(code.toUpperCase());
        if (codes.length > 5) throw new Error('Too many discount codes');
        return request('cart/update.js', { discount: codes.join(','), sections });
      },
      async remove(code, sections) {
        const cart = await request('cart.js');
        const codes = discountCodes(cart).filter(value => value !== code.toUpperCase());
        return request('cart/update.js', { discount: codes.join(','), sections });
      }
    };
  }

  function renderPdpPrice(element, active, formatted) {
    if (element.classList.contains('cg-dn-price--active')) return;
    const original = element.querySelector('[data-missyusa-original]');
    const sale = element.querySelector('[data-missyusa-sale]');
    const amount = element.querySelector('[data-missyusa-sale-amount]');
    if (!original || !sale || !amount) return;
    element.classList.toggle('missyusa-pdp-price--active', active);
    original.hidden = active;
    sale.hidden = !active;
    if (amount.textContent !== formatted) amount.textContent = formatted;
  }

  const api = { campaignState, estimatedPrice, campaignVersion, validContext, productLink,
    discountCodes, discountOutcome, purchaseOutcome, createCartClient, renderPdpPrice };
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
    return;
  }
  if (window.MissyPromotion) return;
  window.MissyPromotion = api;

  function init() {
    const configElement = document.querySelector('[data-missyusa-config]');
    if (!configElement) return;
    let config = JSON.parse(configElement.textContent);
    const client = createCartClient(window.Shopify.routes.root);
    const storageKey = 'missyusa:' + config.code + ':' + config.endsAt;
    const attemptKey = storageKey + ':purchase';
    const contextKey = 'missyusa:pdp';
    let attempt = null;
    try {
      const saved = JSON.parse(sessionStorage.getItem(attemptKey));
      if (saved && typeof saved.token === 'string' && saved.token && /^\d+$/.test(saved.variantId)
        && Number.isInteger(saved.baseline) && saved.baseline >= 0
        && ['pending', 'rejected', 'added'].includes(saved.stage)) attempt = saved;
    } catch { /* Storage is optional. */ }
    let busy = false;
    let activeAdd = null;
    let refreshing = false;
    let syncingCart = false;
    let lastRefresh = 0;
    let lastCart = null;
    let timer;
    let refreshTimer;
    const vendor = () => window['xo-webcomponents'] || window.xobuildercomponents;
    const state = () => campaignState(config);
    const text = key => config.messages[key] || key;
    const pending = () => ['pending', 'rejected'].includes(attempt?.stage);
    const outcome = cart => discountOutcome(cart, config.code, config.productIds, attempt?.variantId);

    function clearContext() {
      try { sessionStorage.removeItem(contextKey); } catch { /* Display fails closed without storage. */ }
    }
    function hasContext() {
      try {
        if (validContext(sessionStorage.getItem(contextKey), config)) return true;
      } catch { /* Display fails closed without storage. */ }
      clearContext();
      return false;
    }
    const eligible = id => config.productIds.map(String).includes(String(id));
    function acceptArrival() {
      const url = new URL(window.location.href);
      if (url.searchParams.get('promo') !== config.campaign) return;
      if (state() === 'active' && config.productId && eligible(config.productId)) {
        try { sessionStorage.setItem(contextKey, campaignVersion(config)); } catch { clearContext(); }
      }
      url.searchParams.delete('promo');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    }
    function updateLinks(root) {
      root.querySelectorAll('[data-missyusa-product-link]').forEach(link => {
        link.dataset.productPath ||= link.getAttribute('href');
        link.setAttribute('href', eligible(link.closest('[data-missyusa-card]').getAttribute('xo-product-id'))
          ? productLink(link.dataset.productPath, config) : link.dataset.productPath);
      });
    }
    function renderPrices() {
      const display = hasContext();
      document.querySelectorAll('[data-missyusa-pdp-price]').forEach(element => {
        const active = display && eligible(element.dataset.productId);
        const formatted = money(estimatedPrice(element.dataset.price, config.percent));
        renderPdpPrice(element, active, formatted);
      });
    }

    function saveAttempt(value) {
      attempt = value;
      try {
        if (value) sessionStorage.setItem(attemptKey, JSON.stringify(value));
        else sessionStorage.removeItem(attemptKey);
      } catch { /* The in-memory attempt still protects this page from duplicate adds. */ }
    }
    function sections() {
      return [...new Set([...document.querySelectorAll('xo-cart[id], xo-cart-mini[id]')]
        .map(element => element.id))].slice(0, 5);
    }
    function money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: lastCart?.currency || config.currency
      }).format(cents / 100);
    }
    function notice(key) {
      window.xoToast?.push({ content: text(key), className: 'xo-cart-add-error', placement: 'top-center', delay: 4000 });
    }
    function syncCart(cart) {
      lastCart = cart;
      // Shopify can report applicable:false until an eligible item is added.
      // Only an eligible line without this code's allocation proves rejection.
      if (hasContext() && discountOutcome(cart, config.code, config.productIds) === 'unavailable') clearContext();
      if (attempt && cart.token !== attempt.token) saveAttempt(null);
      if (cart.sections && vendor()?.xoStore) {
        syncingCart = true;
        vendor().xoStore.set('xo-cart', previous => ({
          ...previous, size: cart.item_count,
          sections: { ...previous.sections, ...Object.fromEntries(
            Object.entries(cart.sections).filter(([, html]) => typeof html === 'string')) }
        }))('missyusa/refresh');
        syncingCart = false;
      }
      render();
    }
    function render() {
      config = JSON.parse(document.querySelector('[data-missyusa-config]').textContent);
      const phase = state();
      renderPrices();
      document.querySelectorAll('[data-missyusa-landing]').forEach(root => {
        root.dataset.phase = phase;
        updateLinks(root);
        root.querySelectorAll('[data-missyusa-buy]').forEach(element => {
          element.hidden = false;
          const button = element.querySelector('button');
          if (button) {
            button.dataset.idleLabel ||= button.textContent;
            button.textContent = activeAdd === element ? text('adding') : button.dataset.idleLabel;
            button.disabled = busy || refreshing || pending() || element.hasAttribute('xo-disabled');
            button.setAttribute('aria-busy', String(activeAdd === element));
            button.lang = activeAdd === element ? 'en' : 'ko';
          }
        });
        root.querySelectorAll('[data-missyusa-swatch]').forEach(input => {
          if (!('stockDisabled' in input.dataset)) input.dataset.stockDisabled = String(input.disabled);
          input.disabled = busy || input.dataset.stockDisabled === 'true';
        });
        root.querySelectorAll('[data-missyusa-estimate]').forEach(element => {
          element.hidden = phase !== 'active' && phase !== 'upcoming';
        });
      });
      clearTimeout(timer);
      const boundary = phase === 'upcoming' ? Date.parse(config.startsAt) : Date.parse(config.endsAt);
      if (config.enabled && boundary > Date.now()) {
        timer = setTimeout(() => { render(); refresh(true); }, Math.min(boundary - Date.now() + 50, 2147483647));
      }
    }

    function reconcile(cart) {
      if (!attempt) return null;
      const result = purchaseOutcome(cart, attempt);
      if (result === 'added') saveAttempt({ ...attempt, stage: 'added' });
      else if (result !== 'pending') saveAttempt(null);
      return result;
    }
    async function refresh(force = false) {
      if (busy || refreshing || (!force && Date.now() - lastRefresh < 1500)) {
        clearTimeout(refreshTimer);
        refreshTimer = setTimeout(() => refresh(true), 1500);
        return;
      }
      clearTimeout(refreshTimer);
      if (!attempt && !hasContext()) { render(); return; }
      refreshing = true;
      render();
      try {
        const cart = attempt ? await client.refresh(sections()) : await client.read();
        const wasPending = pending();
        if (wasPending && !sections().every(id => typeof cart.sections?.[id] === 'string')) throw new Error('Cart rendering unavailable');
        const result = reconcile(cart);
        syncCart(cart);
        if (wasPending && result === 'added') {
          await openVerifiedCart(cart);
          if (state() !== 'active' || outcome(cart) !== 'applied') { clearContext(); notice('unavailable'); }
        }
        lastRefresh = Date.now();
      } catch { /* Keep unresolved purchases locked until a later cart refresh confirms the outcome. */ }
      finally { refreshing = false; render(); }
    }

    function updateCard(card) {
      const selected = card.querySelector('[data-missyusa-swatch]:checked');
      if (!selected) return;
      const variant = JSON.parse(selected.dataset.variant);
      const image = card.querySelector('[data-missyusa-image]');
      if (image && variant.image) {
        image.src = variant.image;
        image.removeAttribute('srcset');
        image.alt = card.dataset.productTitle + ' — ' + variant.title;
      }
      card.querySelectorAll('[data-missyusa-product-link]').forEach(link => {
        const url = new URL(link.dataset.productPath || link.href, window.location.origin);
        url.searchParams.set('variant', variant.id);
        link.dataset.productPath = url.pathname + url.search + url.hash;
      });
      updateLinks(card);
      const add = card.querySelector('[data-missyusa-buy]');
      if (add) add.setAttribute('xo-variant-id', variant.id);
      const label = card.querySelector('[data-missyusa-color]');
      if (label) label.textContent = variant.title;
      card.querySelector('[data-missyusa-current-price]').textContent = money(variant.price);
      card.querySelector('[data-missyusa-estimated-price]').textContent = money(estimatedPrice(variant.price, config.percent));
    }

    async function activate() {
      if (state() !== 'active') throw new Error('Inactive campaign');
      const draft = vendor()?.xoStore?.get('xo-cart-discount')?.discount || '';
      const cart = await client.apply(config.code, draft, sections());
      syncCart(cart);
      if (state() !== 'active') throw new Error('Inactive campaign');
      return cart;
    }
    async function openVerifiedCart(cart) {
      // Section rendering can fail even with HTTP 200. Keep the purchase pending until the drawer is current.
      if (!sections().every(id => typeof cart.sections?.[id] === 'string')) throw new Error('Cart rendering unavailable');
      syncCart(cart);
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      render();
      window.xoModal?.open('cart');
    }

    function addOnce(add, onDispatch) {
      // The vendor swallows HTTP errors. Observe only its synchronous dispatch,
      // forwarding the request unchanged and restoring fetch before yielding.
      const originalFetch = window.fetch;
      let purchase;
      const endpoint = window.Shopify.routes.root + 'cart/add';
      window.fetch = function (url, options) {
        if (url === endpoint && options?.method === 'POST' && options.body?.get?.('id')) {
          onDispatch(String(options.body.get('id')));
          purchase = attempt;
        }
        const response = originalFetch.call(this, url, options);
        if (url !== endpoint || options?.method !== 'POST'
          || options.body?.get?.('id') !== purchase?.variantId) return response;
        return response.then(result => {
          if (attempt === purchase && [404, 422].includes(result.status)) saveAttempt({ ...purchase, stage: 'rejected' });
          return result;
        });
      };
      let request;
      try { request = add.handleAddToCart({ preventDefault() {} }); }
      finally { window.fetch = originalFetch; }
      if (!purchase) throw new Error('No cart addition dispatched');
      let timeout;
      return Promise.race([request, new Promise((_, reject) => {
        timeout = setTimeout(() => reject(new Error('Addition outcome unknown')), 15000);
      })]).finally(() => clearTimeout(timeout));
    }

    async function handleClick(event) {
      const card = event.target.closest('[data-missyusa-card]');
      const add = event.target.closest('xo-cart-add');
      const link = event.target.closest('[data-missyusa-product-link]');
      if (link) { updateLinks(card); return; }
      const pdp = add?.closest('xo-product[xo-product-information]');
      // The preflight read can outlive the display context or eligible settings.
      // An already-started purchase remains locked independently of both.
      if (pdp && (busy || (pending() && attempt?.source === 'pdp'))) {
        event.preventDefault(); event.stopImmediatePropagation(); return;
      }
      if (!card && !(pdp && eligible(pdp.getAttribute('xo-product-id'))
        && hasContext())) return;
      if (!add) return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (busy || pending() || (!pdp && refreshing)) return;
      if (state() !== 'active') { notice('error'); return; }
      const variantId = add.getAttribute('xo-variant-id') || pdp?.querySelector('[data-missyusa-pdp-price]')?.dataset.variantId;
      if (!/^\d+$/.test(variantId) || add.hasAttribute('xo-disabled')
        || add.querySelector('button')?.disabled || card?.querySelector('[data-missyusa-swatch]:checked')?.disabled) {
        notice('error'); return;
      }
      busy = true; activeAdd = add;
      render();
      let additionStarted = false;
      const opened = add.getAttribute('xo-cart-opened');
      try {
        await Promise.race([customElements.whenDefined('xo-cart-add'),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Cart component unavailable')), 10000))]);
        if (typeof add.handleAddToCart !== 'function') throw new Error('Cart component unavailable');
        const before = pdp ? await client.read() : await activate();
        if (!before.token) throw new Error('Cart identity unavailable');
        if (state() !== 'active' && !pdp) throw new Error('Inactive campaign');
        // Variant rendering may replace the clicked button during the preflight read.
        if (!add.isConnected || add.hasAttribute('xo-disabled') || (pdp && add.querySelector('button')?.disabled)) {
          throw new Error('Cart button changed');
        }
        add.setAttribute('xo-cart-opened', 'false');
        // The vendor owns inventory and cart additions. It must be called only once per purchase attempt.
        await addOnce(add, dispatchedVariantId => {
          saveAttempt({ token: before.token, variantId: dispatchedVariantId,
            baseline: variantQuantity(before, dispatchedVariantId),
            stage: 'pending', source: pdp ? 'pdp' : 'landing' });
          additionStarted = true;
        });
        const cart = await client.refresh(sections());
        if (!sections().every(id => typeof cart.sections?.[id] === 'string')) throw new Error('Cart rendering unavailable');
        const result = reconcile(cart);
        if (result === 'added') {
          await openVerifiedCart(cart);
          if (state() !== 'active' || outcome(cart) !== 'applied') { clearContext(); notice('unavailable'); }
        } else {
          syncCart(cart);
          if (!pdp || !vendor()?.getCartState()?.addErrorMessage) {
            notice(result === 'not_added' ? 'error' : 'added_error');
          }
        }
      } catch {
        if (!pdp || !additionStarted || !vendor()?.getCartState()?.addErrorMessage) {
          notice(additionStarted ? 'added_error' : 'error');
        }
      } finally {
        if (opened === null) add.removeAttribute('xo-cart-opened');
        else add.setAttribute('xo-cart-opened', opened);
        busy = false; activeAdd = null;
        render();
      }
    }
    document.addEventListener('click', handleClick, true);
    document.addEventListener('auxclick', event => {
      if (event.button === 1) handleClick(event);
    }, true);
    document.addEventListener('change', event => {
      if (event.target.matches('[data-missyusa-swatch]') && !busy) updateCard(event.target.closest('[data-missyusa-card]'));
    });
    document.addEventListener('shopify:section:load', render);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) { render(); refresh(true); }
    });
    window.addEventListener('pageshow', () => { render(); refresh(true); });
    if (vendor()?.cartSubscribe) vendor().cartSubscribe(cart => {
      if (!syncingCart && cart.addIdLoading === '' && cart.changeLineLoading === -1) refresh();
    });
    if (window.cartFormSubscribe) window.cartFormSubscribe(() => requestAnimationFrame(render));
    if (window.productSubscribe) window.productSubscribe(() => requestAnimationFrame(render));
    // Observe confirmed explicit removals, including on an empty cart. Forward every
    // request unchanged; absence of allocations alone cannot prove an empty-cart removal.
    const originalFetch = window.fetch;
    window.fetch = function (input, options) {
      const response = originalFetch.apply(this, arguments);
      const url = new URL(typeof input === 'string' ? input : input.url || input.href, window.location.origin);
      const updatePath = window.Shopify.routes.root + 'cart/update';
      if (url.origin === window.location.origin && [updatePath, updatePath + '.js'].includes(url.pathname)
        && options?.method?.toUpperCase() === 'POST' && hasContext()) {
        let discount;
        try {
          discount = typeof options.body === 'string' ? JSON.parse(options.body).discount : options.body?.get?.('discount');
        } catch { /* An unrelated request body is not a removal signal. */ }
        if (typeof discount === 'string' && !discount.split(',').some(code => code.trim().toUpperCase() === config.code.toUpperCase())) {
          response.then(async result => {
            if (!result.ok) return;
            const cart = await result.clone().json();
            if (cart.token && Array.isArray(cart.items)) { clearContext(); render(); }
          }).catch(() => { /* Failed or uncertain mutations do not confirm removal. */ });
        }
      }
      return response;
    };
    // Variant rendering replaces the price nodes asynchronously after the form changes.
    new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1
        && (node.matches('[data-missyusa-pdp-price]') || node.querySelector('[data-missyusa-pdp-price]'))))) render();
    }).observe(document.body, { childList: true, subtree: true });
    acceptArrival();
    render();
    refresh(true);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
